import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
  Chip,
  Stack,
  Paper,
  Fade,
  Grow,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  IconButton,
  Switch,
  Divider,
  InputAdornment,
  Tooltip,
  Skeleton,
  Autocomplete,
  AlertTitle,
  LinearProgress,
} from "@mui/material";
import {
  PhoneInTalk,
  Upload,
  Settings,
  Campaign,
  Schedule,
  Sms,
  AudioFile,
  ContactPhone,
  Info,
  Warning,
  CheckCircle as SuccessIcon,
  Close,
  WhatsApp,
  Message,
  Tune,
  Preview,
  AccessTime,
  ErrorOutline,
} from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import useApiEp from "../../hooks/useApiEp";
import MuPb from "../../widgets/MuPb";
import { API_ENDPOINT } from "../../utils/constants";
import { getHeader, getToken } from "../../utils/util";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import BatchingSettings from "./BatchingSettings";
import VoiceInstructionsBanner from "./VoiceInstructionsBanner";
import DatabaseAIAssistant from "../campaign/contacts/DatabaseAIAssistant";

// ─── Time helpers ────────────────────────────────────────────────────────────
const parseTime = (timeStr) => {
  const [hours, minutes] = timeStr.split(":").map(Number);
  return { hours, minutes };
};

const toMinutes = (date) => date.getHours() * 60 + date.getMinutes();

const currentBlackoutWindow = (date, nonActiveHours) => {
  const current = toMinutes(date);
  return (
    nonActiveHours.find(({ start, end }) => {
      const s = parseTime(start);
      const e = parseTime(end);
      const startMins = s.hours * 60 + s.minutes;
      const endMins = e.hours * 60 + e.minutes;
      if (startMins > endMins) return current >= startMins || current < endMins;
      return current >= startMins && current < endMins;
    }) || null
  );
};

const advanceToActiveSlot = (date, activeDays, nonActiveHours) => {
  let result = new Date(date);
  const MAX_ITERS = 14 * 1440;
  for (let i = 0; i < MAX_ITERS; i++) {
    const dow = result.getDay();
    if (!activeDays.includes(dow)) {
      result.setDate(result.getDate() + 1);
      result.setHours(0, 0, 0, 0);
      continue;
    }
    const win = currentBlackoutWindow(result, nonActiveHours);
    if (win) {
      const e = parseTime(win.end);
      const blackoutEnd = new Date(result);
      blackoutEnd.setHours(e.hours, e.minutes, 0, 0);
      if (blackoutEnd <= result) blackoutEnd.setDate(blackoutEnd.getDate() + 1);
      result.setTime(blackoutEnd.getTime());
      continue;
    }
    return result;
  }
  return result;
};

const generateBatchScheduleTimes = (baseTime, totalBatches, batchSettings) => {
  const { intervalMinutes, activeDays, nonActiveHours } = batchSettings;
  const times = [];
  let cursor = advanceToActiveSlot(
    new Date(baseTime),
    activeDays,
    nonActiveHours
  );
  times.push(new Date(cursor));
  for (let i = 1; i < totalBatches; i++) {
    cursor = new Date(cursor.getTime() + intervalMinutes * 60 * 1000);
    cursor = advanceToActiveSlot(cursor, activeDays, nonActiveHours);
    times.push(new Date(cursor));
  }
  return times;
};

const getLineCount = (text) => {
  if (!text) return 0;
  return text
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l !== "").length;
};

const formatDateIST = (date) =>
  date.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

const DTMF_INPUTS = [
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "0",
  "#",
  "*",
];
const WA_CONDITIONS = ["any", "answered", "noagent", "missed"];
const emptyDtmfRow = () => ({
  input: "1",
  type: "whatsapp",
  condition: "",
  templateId: "",
});
const emptyBlackout = () => ({ start: "22:00", end: "08:00" });
const emptyRangeRow = () => ({
  templateId: "",
  minDuration: "10",
  maxDuration: "60",
});

export const DAY_LABELS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];
export const sectionHeaderSx = {
  p: 3,
  background: "linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)",
  borderBottom: "1px solid #e5e7eb",
};

// ─── Component ───────────────────────────────────────────────────────────────
const ImprovedVoiceCampaignForm = () => {
  const { data: voices, isLoading } = useApiEp(
    `/voice/voice-files-status/approved`
  );
  const { data: existingBaseFiles } = useApiEp(`/voice/existing-voice-clips`);

  // ── Agent groups ───────────────────────────────────────────────────────────
  const [agentGroupsKey, setAgentGroupsKey] = useState(0);
  const [viewGroupDialogOpen, setViewGroupDialogOpen] = useState(false);
  const [viewingGroup, setViewingGroup] = useState(null);
  const { data: agentGroups, isLoading: loadingGroups } = useApiEp(
    `/voice/agent-groups?key=${agentGroupsKey}`
  );
  const [selectedAgentGroups, setSelectedAgentGroups] = useState([]);

  // ── Create group dialog ────────────────────────────────────────────────────
  const [createGroupDialog, setCreateGroupDialog] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupAgents, setNewGroupAgents] = useState([
    { agentNumber: "", agentName: "" },
  ]);
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);
  const [useFullRange, setUseFullRange] = useState(true);
  // ── Main form state ────────────────────────────────────────────────────────
  const [formData, setFormData] = useState({
    templateId: "0",
    campaignName: "",
    location: "Bangalore",
    contactTab: "oldBaseFile",
    contactList: "",
    newContactsFile: null,
    baseName: "",
    oldBaseId: "",
    welcomePId: "",
    menuPId: "",
    noInputPId: "",
    wrongInputPId: "",
    thanksPId: "",
    dtmf: "",
    menuWaitTime: "5",
    rePrompt: "0",
    agentRows:
      '{"patchList":[{"groupId":"99","agentDtmf":"1","groupName":"Support"}]}',
    noAgentId: "102",
    retries: "",
    retryInterval: "",
    sendSmsSuccess: false,
    sendSmsFail: false,
    sendLater: false,
    scheduleTime: "",
  });

  // ── Fallback settings ──────────────────────────────────────────────────────
  const [fallbackEnabled, setFallbackEnabled] = useState(false);

  // ── Range settings — now arrays of rows ───────────────────────────────────
  const [rangeWa, setRangeWa] = useState({
    enabled: false,
    rows: [emptyRangeRow()],
  });
  const [rangeRcs, setRangeRcs] = useState({
    enabled: false,
    rows: [emptyRangeRow()],
  });
  const [aiDbFilter, setAiDbFilter] = useState(null);

  // ── Range row helpers ──────────────────────────────────────────────────────
  const addRangeRow = (setter) =>
    setter((p) => ({ ...p, rows: [...p.rows, emptyRangeRow()] }));

  const removeRangeRow = (setter, idx) =>
    setter((p) => ({ ...p, rows: p.rows.filter((_, i) => i !== idx) }));

  const updateRangeRow = (setter, idx, field, value) =>
    setter((p) => {
      const rows = [...p.rows];
      rows[idx] = { ...rows[idx], [field]: value };
      return { ...p, rows };
    });

  // ── Batch settings ─────────────────────────────────────────────────────────
  const [batchSettings, setBatchSettings] = useState({
    enabled: false,
    batchSize: "5000",
    interval: "60",
    activeDays: [1, 2, 3, 4, 5],
    nonActiveHours: [emptyBlackout()],
  });

  const [executiveSettings, setExecutiveSettings] = useState(true);

  // ── Failed Messages Fallback ───────────────────────────────────────────────
  const [failedMessagesFallback, setFailedMessagesFallback] = useState(false);
  const [failedMsgChannel, setFailedMsgChannel] = useState("whatsapp");
  const [failedMsgTemplateId, setFailedMsgTemplateId] = useState("");

  const [dtmfRows, setDtmfRows] = useState([emptyDtmfRow()]);

  // ── Misc state ─────────────────────────────────────────────────────────────
  const [tabValue, setTabValue] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [baseFileId, setBaseFileId] = useState(null);
  const [isUploadingContacts, setIsUploadingContacts] = useState(false);
  const [saveToListDialog, setSaveToListDialog] = useState(false);
  const [pendingContacts, setPendingContacts] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [enabledPrompts, setEnabledPrompts] = useState({
    welcomePId: false,
    noInputPId: false,
    wrongInputPId: false,
    thanksPId: false,
  });
  const [successDialog, setSuccessDialog] = useState({
    open: false,
    campaignId: "",
    message: "",
  });

  // ── Error modal ────────────────────────────────────────────────────────────
  const [errorDialog, setErrorDialog] = useState({ open: false, message: "" });

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState("");

  const batchContactsRef = useRef([]);
  const [useContactsList, setUseContactsList] = useState(false);
  const { data: groups, isLoading: isLoadingGroups } = useApiEp(
    `/user/group-list`,
    useContactsList
  );
  const { data: schemaData, isLoading: isSchemaLoading } = useApiEp(
    `/user/get-contact-schema?groupName=${selectedGroup}`
  );
  const count = getLineCount(formData.contactList);
  const [searchParams] = useSearchParams();
  const contactGroupId = searchParams.get("id");
  const { data: contactGroupDetail, isLoading: isCdLoading } = useApiEp(
    `/campaign/campaign-group-count/${contactGroupId}`,
    !!contactGroupId
  );

  // ── Contact tabs ───────────────────────────────────────────────────────────
  const contactTabs = useMemo(() => {
    if (batchSettings.enabled) {
      return [
        { label: "Manual Entry", value: 0 },
        { label: "Upload File", value: 1 },
      ];
    }
    return [
      { label: "Manual Entry", value: 0 },
      { label: "Upload File", value: 1 },
      { label: "Existing Files", value: 2 },
    ];
  }, [batchSettings.enabled]);

  const retryOverlapWarning = useMemo(() => {
    // Only check if batching is enabled, retries are > 0, and there are blackout windows
    if (
      !batchSettings.enabled ||
      !batchSettings.nonActiveHours?.length ||
      !formData.retries ||
      formData.retries === "0"
    ) {
      return false;
    }

    const retries = parseInt(formData.retries, 10);
    // retryInterval is in seconds (e.g., 900 = 15 mins), convert to minutes
    const retryIntervalMins = parseInt(formData.retryInterval, 10) / 60;
    const totalRetryDurationMins = retries * retryIntervalMins;
    const batchIntervalMins = parseInt(batchSettings.interval, 10) || 0;

    const now = new Date();

    // Helper to check if a specific Date falls within any blackout window
    const isTimeInBlackout = (dateToCheck) => {
      const hours = dateToCheck.getHours();
      const minutes = dateToCheck.getMinutes();
      const timeInMins = hours * 60 + minutes;

      return batchSettings.nonActiveHours.some((window) => {
        if (!window.start || !window.end) return false;

        const [startH, startM] = window.start.split(":").map(Number);
        const [endH, endM] = window.end.split(":").map(Number);

        const blackoutStart = startH * 60 + startM;
        const blackoutEnd = endH * 60 + endM;

        if (blackoutStart > blackoutEnd) {
          // Overnight window (e.g., 22:00 to 08:00)
          return timeInMins >= blackoutStart || timeInMins <= blackoutEnd;
        } else {
          // Same-day window (e.g., 13:00 to 14:00)
          return timeInMins >= blackoutStart && timeInMins <= blackoutEnd;
        }
      });
    };

    // Simulate the first 5 batches to see if their extended retry window hits a blackout
    const BATCHES_TO_SIMULATE = 5;

    for (let i = 0; i < BATCHES_TO_SIMULATE; i++) {
      // Start time of this specific batch
      const batchStart = new Date(
        now.getTime() + i * batchIntervalMins * 60000
      );
      // End time after all retries are exhausted for this batch
      const retryEnd = new Date(
        batchStart.getTime() + totalRetryDurationMins * 60000
      );

      // If the time the retries end falls into a blackout window, trigger the warning
      if (isTimeInBlackout(retryEnd)) {
        return true;
      }
    }

    return false;
  }, [batchSettings, formData.retries, formData.retryInterval]);
  // ── Validation helpers ─────────────────────────────────────────────────────
  const validatePhoneNumber = (n) =>
    /^\d{10}$/.test(n.replace(/\D/g, "").slice(-10)) &&
    n.replace(/\D/g, "").length === 10;

  const extractPhoneNumbers = (text) => {
    const lines = text.split(/[\n,;]/);
    const numbers = [],
      errors = [];
    lines.forEach((line, i) => {
      const cleaned = line.trim().replace(/\D/g, "");
      if (cleaned)
        validatePhoneNumber(cleaned)
          ? numbers.push(cleaned)
          : errors.push(`Line ${i + 1}: Invalid "${line.trim()}"`);
    });
    return { numbers, errors };
  };

  const getGroupById = (id) =>
    agentGroups?.find((g) => String(g.groupId) === String(id));

  // ── Agent group creation ───────────────────────────────────────────────────
  const handleCreateGroup = async () => {
    const agents = newGroupAgents.map((a) => ({
      agentNumber: a.agentNumber.replace(/\D/g, "").slice(0, 10),
      agentName: a.agentName.trim() || a.agentNumber,
    }));
    setIsCreatingGroup(true);
    try {
      const res = await fetch(`${API_ENDPOINT}/voice/agent-groups`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ groupName: newGroupName.trim(), agents }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Creation failed");
      setAgentGroupsKey((p) => p + 1);
      toast.success(`Group "${newGroupName}" created`);
      setCreateGroupDialog(false);
      setNewGroupName("");
      setNewGroupAgents([{ agentNumber: "", agentName: "" }]);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setIsCreatingGroup(false);
    }
  };

  // ── Contact parsing ────────────────────────────────────────────────────────
  const parseCSVFile = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const lines = e.target.result.split(/\r?\n/);
          const numbers = [],
            errors = [];
          lines.forEach((line, idx) => {
            if (!line.trim()) return;
            let found = false;
            for (let col of line.split(/[,\t]/)) {
              const cleaned = col.trim().replace(/\D/g, "");
              if (cleaned && validatePhoneNumber(cleaned)) {
                numbers.push(cleaned);
                found = true;
                break;
              }
            }
            if (!found && line.trim())
              errors.push(`Line ${idx + 1}: No valid phone`);
          });
          resolve({ numbers, errors });
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsText(file);
    });

  const parseTXTFile = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(extractPhoneNumbers(e.target.result));
      reader.onerror = () => reject(reader.error);
      reader.readAsText(file);
    });

  const uploadContactsToAPI = async (contacts, saveToList) => {
    const res = await fetch(`${API_ENDPOINT}/voice/upload-contacts`, {
      method: "POST",
      ...getHeader(), // Make sure this includes { "Content-Type": "application/json" }
      body: JSON.stringify({ contacts, saveToList }),
    });

    if (!res.ok) {
      // Attempt to parse the JSON error body from the backend
      let errorMessage = "Upload failed due to an unknown error";
      try {
        const errorData = await res.json();
        // Look for the 'message' key you are returning from both 400 and 500 responses
        errorMessage = errorData.message || errorMessage;
      } catch (parseError) {
        // Fallback if the server didn't return valid JSON (e.g., 502 Bad Gateway)
        console.error("Could not parse error response", parseError);
      }

      // Throw the actual server message so the UI can catch it
      throw new Error(errorMessage);
    }

    const data = await res.json();
    return data.baseFileId;
  };

  const handleSaveToListConfirm = async (saveToList) => {
    setSaveToListDialog(false);
    setIsUploadingContacts(true);
    try {
      const id = await uploadContactsToAPI(pendingContacts, saveToList);
      setBaseFileId(id);
      toast.success(`Contacts uploaded! Base ID: ${id}`);
    } catch {
      toast.error("Failed to upload contacts");
    } finally {
      setIsUploadingContacts(false);
      setPendingContacts([]);
    }
  };

  const parseContacts = async () => {
    let contacts = [],
      errors = [];
    if (tabValue === 0) {
      if (!formData.contactList.trim()) {
        toast.error("Enter at least one contact");
        return null;
      }
      ({ numbers: contacts, errors } = extractPhoneNumbers(
        formData.contactList
      ));
    } else if (tabValue === 1) {
      if (!formData.newContactsFile) {
        toast.error("Select a file");
        return null;
      }
      if (!formData.baseName.trim()) {
        toast.error("Enter base file name");
        return null;
      }
      const ext = formData.newContactsFile.name.split(".").pop().toLowerCase();
      ({ numbers: contacts, errors } =
        ext === "csv"
          ? await parseCSVFile(formData.newContactsFile)
          : await parseTXTFile(formData.newContactsFile));
    }
    if (contacts.length > 1000000) {
      toast.error(`Max 10 lakh contacts. Found: ${contacts.length}`);
      return null;
    }
    const unique = [...new Set(contacts)];
    setValidationErrors(errors);
    return unique;
  };

  const validateAndPrepareContacts = async () => {
    try {
      if (tabValue === 2) {
        if (!formData.oldBaseId) {
          toast.error("Select existing file");
          return false;
        }
        setBaseFileId(formData.oldBaseId);
        return true;
      }
      const contacts = await parseContacts();
      if (!contacts) return false;
      setPendingContacts([...contacts]);
      setSaveToListDialog(true);
      return true;
    } catch {
      toast.error("Error processing contacts");
      return false;
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    setFormData((p) => ({
      ...p,
      [name]:
        type === "checkbox" ? checked : type === "file" ? files[0] : value,
    }));
    if (validationErrors.length > 0) setValidationErrors([]);
    if (["contactList", "newContactsFile"].includes(name)) setBaseFileId(null);
  };

  const handleTabChange = (_, v) => {
    setTabValue(v);
    setFormData((p) => ({
      ...p,
      contactTab: ["contactList", "newBaseFile", "oldBaseFile"][v],
    }));
    setValidationErrors([]);
    setBaseFileId(null);
  };
  // ── Build fallback payload ─────────────────────────────────────────────────
  // rangeSettingsWhatsapp and rangeSettingsRcs are now arrays
  const buildFallbackPayload = () => {
    if (!fallbackEnabled) return {};
    const payload = {};

    if (rangeWa.enabled) {
      const validRows = rangeWa.rows.filter((r) => r.templateId);
      if (validRows.length > 0) {
        payload.rangeSettingsWhatsapp = validRows.map((r) => ({
          campaign: r.templateId,
          minDuration: Number(r.minDuration),
          maxDuration: Number(r.maxDuration),
        }));
      }
    }

    if (rangeRcs.enabled) {
      const validRows = rangeRcs.rows.filter((r) => r.templateId);
      if (validRows.length > 0) {
        payload.rangeSettingsRcs = validRows.map((r) => ({
          campaign: r.templateId,
          minDuration: Number(r.minDuration),
          maxDuration: Number(r.maxDuration),
        }));
      }
    }

    const dtmfSettings = dtmfRows
      .filter((r) => r.input && r.templateId)
      .map((r) => ({
        input: r.input,
        type: r.type,
        ...(r.condition ? { condition: r.condition } : {}),
        campaign: r.templateId,
      }));

    if (dtmfSettings.length > 0) payload.dtmfSettings = dtmfSettings;
    return payload;
  };

  const buildBatchPayload = () => {
    if (!batchSettings.enabled) return {};
    return {
      enabled: true,
      batchSize: Number(batchSettings.batchSize),
      intervalMinutes: Number(batchSettings.interval),
      activeDays: batchSettings.activeDays,
      nonActiveHours: batchSettings.nonActiveHours,
    };
  };

  const buildBatchPreview = (contacts) => {
    const batchSize = Math.max(1, Number(batchSettings.batchSize));
    const totalBatches = Math.ceil(contacts.length / batchSize);
    const scheduleTimes = generateBatchScheduleTimes(new Date(), totalBatches, {
      intervalMinutes: Number(batchSettings.interval),
      activeDays: batchSettings.activeDays,
      nonActiveHours: batchSettings.nonActiveHours,
    });
    const schedules = scheduleTimes.map((t, i) => ({
      batch: i + 1,
      contacts: Math.min(batchSize, contacts.length - i * batchSize),
      scheduledAt: formatDateIST(t),
      rawDate: t,
    }));
    return {
      totalContacts: contacts.length,
      totalBatches,
      batchSize,
      intervalMinutes: Number(batchSettings.interval),
      schedules,
    };
  };

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleActualSubmit = async (
    passedBaseFileId,
    contactsArray = null,
    groupId = null
  ) => {
    setIsSubmitting(true);
    const effectiveBaseFileId = passedBaseFileId ?? baseFileId;
    try {
      let agentRowsPayload = "";
      if (formData.templateId === "2") {
        agentRowsPayload =
          selectedAgentGroups.length > 0
            ? JSON.stringify({
                patchList: selectedAgentGroups.map((g) => ({
                  groupId: g.groupId,
                  agentDtmf: g.dtmf,
                  groupName: g.groupName,
                })),
              })
            : "";
      } else {
        agentRowsPayload = formData.agentRows;
      }

      let contactsField = {};
      if (groupId) {
        contactsField = {
          contactGroupId: groupId,
          useFullRange,
          contactsGroupStartIndex: formData?.contactsGroupStartIndex,
          contactsGroupEndIndex: formData?.contactsGroupEndIndex,
        };
        if (
          contactGroupDetail?.isDb &&
          aiDbFilter?.contactGroupId === groupId &&
          aiDbFilter?.fullFilter != null
        ) {
          contactsField.dbquery = aiDbFilter.fullFilter;
        }
      } else if (batchSettings.enabled) {
        contactsField = { contacts: contactsArray };
      } else {
        contactsField = { baseFileId: effectiveBaseFileId };
      }

      const failedMsgFallbackPayload =
        failedMessagesFallback && failedMsgTemplateId
          ? {
              enabled: true,
              channel: failedMsgChannel,
              templateId: failedMsgTemplateId,
            }
          : { enabled: false };

      const payload = {
        campaignName: formData.campaignName,
        templateId: formData.templateId,
        ...contactsField,
        welcomePId: formData.welcomePId,
        menuPId: formData.menuPId,
        noInputPId: formData.noInputPId,
        wrongInputPId: formData.wrongInputPId,
        thanksPId: formData.thanksPId,
        dtmf: formData.dtmf,
        menuWaitTime: formData.menuWaitTime,
        rePrompt: formData.rePrompt,
        agentRows: agentRowsPayload,
        noAgentId: formData.noAgentId,
        sendSmsSuccess: formData.sendSmsSuccess,
        sendSmsFail: formData.sendSmsFail,
        sendSmsDtmf: false,
        sendLater: formData.sendLater,
        scheduleTime: formData.scheduleTime,
        retries: formData.retries,
        retryInterval: formData.retryInterval,
        location: formData.location,
        settings: buildFallbackPayload(),
        batchSettings: buildBatchPayload(),
        executiveSettings,
        failedMessagesFallback: failedMsgFallbackPayload,
      };

      const res = await fetch(`${API_ENDPOINT}/voice/campaign/compose`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) {
        setSuccessDialog({
          open: true,
          campaignId: data.campaignId,
          message: data.message || "Campaign launched!",
        });
      } else {
        throw new Error(data.message || "Launch failed");
      }
    } catch (e) {
      setErrorDialog({
        open: true,
        message: e.message || "Failed to launch campaign. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (baseFileId && pendingContacts.length === 0 && tabValue !== 2)
      handleActualSubmit();
  }, [baseFileId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (contactGroupId) {
      await handleActualSubmit(null, null, contactGroupId);
      return;
    }
    if (batchSettings.enabled) {
      try {
        const contacts = await parseContacts();
        if (!contacts || contacts.length === 0) {
          toast.error("No valid contacts found");
          return;
        }
        if (batchSettings.activeDays.length === 0) {
          toast.error("Select at least one active day");
          return;
        }
        const preview = buildBatchPreview(contacts);
        batchContactsRef.current = contacts;
        setPreviewData(preview);
        setPreviewOpen(true);
      } catch {
        toast.error("Error processing contacts");
      }
      return;
    }
    if (tabValue === 2) {
      if (!formData.oldBaseId) {
        toast.error("Please select existing contact file");
        return;
      }
      await handleActualSubmit(formData.oldBaseId);
      return;
    }
    if (!baseFileId) {
      await validateAndPrepareContacts();
      return;
    }
    await handleActualSubmit();
  };

  const handlePreviewConfirm = async () => {
    setPreviewOpen(false);
    await handleActualSubmit(null, batchContactsRef.current);
  };

  // ── DTMF row helpers ───────────────────────────────────────────────────────
  const addDtmfRow = () => setDtmfRows([...dtmfRows, emptyDtmfRow()]);
  const removeDtmfRow = (i) =>
    setDtmfRows(dtmfRows.filter((_, idx) => idx !== i));
  const updateDtmfRow = (i, field, value) => {
    const rows = [...dtmfRows];
    rows[i] = { ...rows[i], [field]: value };
    setDtmfRows(rows);
  };

  // ── Static options ─────────────────────────────────────────────────────────
  const oldBaseOptions = [
    { value: "", label: "Select File" },
    ...(existingBaseFiles || []).map((item) => ({
      value: item.baseId,
      label: item.baseName || item.baseId,
    })),
  ];
  const webhookOptions = [
    { value: "", label: "Select Webhook" },
    { value: "301", label: "Voice Hangup" },
  ];

  if (isLoading) return <MuPb />;

  const wabaCampaigns = contactGroupDetail?.wabaCampaigns || [];
  const rcsCampaigns = contactGroupDetail?.rcsCampaigns || [];
  const failedMsgTemplates =
    failedMsgChannel === "whatsapp" ? wabaCampaigns : rcsCampaigns;

  // ── Reusable range rows renderer ───────────────────────────────────────────
  const renderRangeRows = ({
    state,
    setter,
    campaigns,
    accentColor,
    channelIcon,
    label,
    bgColor,
    borderColor,
  }) => (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        borderRadius: 2,
        bgcolor: state.enabled ? bgColor : "#fafafa",
        borderColor: state.enabled ? borderColor : "#e5e7eb",
      }}
    >
      {/* Header row with toggle */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        mb={state.enabled ? 2 : 0}
      >
        {channelIcon}
        <Typography fontWeight={500} sx={{ flex: 1 }}>
          {label}
        </Typography>
        <Switch
          size="small"
          checked={state.enabled}
          onChange={(e) => setter((p) => ({ ...p, enabled: e.target.checked }))}
        />
      </Stack>

      {state.enabled && (
        <Stack spacing={2}>
          {state.rows.map((row, idx) => (
            <Paper
              key={idx}
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: "#fff",
                borderColor: "#e5e7eb",
              }}
            >
              <Grid container spacing={2} alignItems="flex-start">
                {/* Campaign */}
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Campaign</InputLabel>
                    <Select
                      label="Campaign"
                      value={row.templateId}
                      onChange={(e) =>
                        updateRangeRow(
                          setter,
                          idx,
                          "templateId",
                          e.target.value
                        )
                      }
                    >
                      <MenuItem value="">
                        <em>Select campaign</em>
                      </MenuItem>
                      {campaigns.map((t) => (
                        <MenuItem key={t.campaignName} value={t.campaignName}>
                          <Stack>
                            <Typography variant="body2" fontWeight={500}>
                              {t.campaignName}
                            </Typography>
                            {t.templateId && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {t.templateId}
                              </Typography>
                            )}
                          </Stack>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Min duration */}
                <Grid item xs={5} md={3}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Min Duration (sec)"
                    type="number"
                    value={row.minDuration}
                    onChange={(e) =>
                      updateRangeRow(setter, idx, "minDuration", e.target.value)
                    }
                    inputProps={{ min: 0 }}
                  />
                </Grid>

                {/* Max duration */}
                <Grid item xs={5} md={3}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Max Duration (sec)"
                    type="number"
                    value={row.maxDuration}
                    onChange={(e) =>
                      updateRangeRow(setter, idx, "maxDuration", e.target.value)
                    }
                    inputProps={{ min: 0 }}
                  />
                </Grid>

                {/* Remove button */}
                <Grid
                  item
                  xs={2}
                  md={2}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                  }}
                >
                  <Tooltip title="Remove this range condition">
                    <span>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeRangeRow(setter, idx)}
                        disabled={state.rows.length === 1}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Grid>

                {/* Summary chips */}
                {row.templateId && (
                  <Grid item xs={12}>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      <Chip
                        size="small"
                        label={`Campaign: ${row.templateId}`}
                        color="success"
                        variant="outlined"
                      />
                      <Chip
                        size="small"
                        label={`${row.minDuration}s – ${row.maxDuration}s`}
                        color="primary"
                        variant="outlined"
                      />
                    </Stack>
                  </Grid>
                )}
              </Grid>
            </Paper>
          ))}

          {/* Add condition button */}
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => addRangeRow(setter)}
            sx={{ alignSelf: "flex-start" }}
          >
            Add Another Condition
          </Button>
        </Stack>
      )}
    </Paper>
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f3f4f6 0%, #ffffff 100%)",
        py: 6,
        px: 2,
      }}
    >
      <Box sx={{ maxWidth: 1000, mx: "auto" }}>
        {/* Header */}
        <Fade in timeout={800}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 3 },
              mb: 2,
              borderRadius: 3,
              background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
              display: "flex",
              flexDirection: "column", // Forces vertical stacking on all screen sizes
              alignItems: "stretch", // Ensures the banner takes up the full width
              gap: 2.5, // Adjusted gap for vertical rhythm
            }}
          >
            {/* Top Section: Icon & Title */}
            <Stack direction="row" alignItems="center" spacing={2.5}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: "white",
                  display: "flex",
                  boxShadow: "0 4px 12px rgba(59,130,246,0.15)",
                }}
              >
                <PhoneInTalk sx={{ fontSize: 36, color: "#3b82f6" }} />
              </Box>
              <Typography
                variant="h5"
                fontWeight={700}
                fontFamily="'Sora', sans-serif"
                letterSpacing="-0.02em"
                color="text.primary"
              >
                Voice Campaign Studio
              </Typography>
            </Stack>

            {/* Bottom Section: Instructions Banner */}
            <VoiceInstructionsBanner />
          </Paper>
        </Fade>

        {baseFileId && !batchSettings.enabled && (
          <Alert severity="success" sx={{ mb: 4 }}>
            Contacts ready! Base File ID: <strong>{baseFileId}</strong>
          </Alert>
        )}
        {validationErrors.length > 0 && (
          <Alert severity="warning" sx={{ mb: 4 }}>
            {validationErrors.map((e, i) => (
              <div key={i}>• {e}</div>
            ))}
          </Alert>
        )}

        <Grow in timeout={1200}>
          <Card
            sx={{ borderRadius: 4, boxShadow: "0 20px 60px rgba(0,0,0,0.05)" }}
          >
            <Box
              sx={{
                p: 2,
                background: "linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)",
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Campaign sx={{ color: "#3b82f6", fontSize: 28 }} />
                <Typography variant="h6" fontWeight={600}>
                  Campaign Configuration
                </Typography>
              </Stack>
            </Box>

            <CardContent sx={{ p: 4 }}>
              <form onSubmit={handleSubmit}>
                <Grid container spacing={4}>
                  {/* ── Template ──────────────────────────────────────────── */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      name="campaignName"
                      label="Campaign Name"
                      value={formData.campaignName}
                      onChange={handleChange}
                      inputProps={{ maxLength: 60 }} // extra safety net
                      helperText="Only letters, numbers, hyphens, and underscores are allowed. Max 60 characters."
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Paper elevation={0}>
                      {/* Header */}
                      <Stack
                        direction="row"
                        spacing={1.5}
                        mb={2}
                        alignItems="center"
                      >
                        <Settings sx={{ color: "#3b82f6" }} />
                        <Typography fontWeight={600}>
                          Select IVR Template
                        </Typography>
                      </Stack>

                      {/* Compact Select Dropdown */}
                      <FormControl fullWidth>
                        <Select
                          name="templateId"
                          value={formData.templateId}
                          onChange={handleChange}
                          displayEmpty
                          // renderValue controls what is shown when the dropdown is CLOSED
                          renderValue={(selected) => {
                            const options = [
                              { value: "0", label: "Simple IVR", icon: "📞" },
                              { value: "1", label: "DTMF Input", icon: "⌨️" },
                              { value: "2", label: "Call Patch", icon: "🔄" },
                            ];
                            const selectedOption = options.find(
                              (opt) => opt.value === selected
                            );

                            if (!selectedOption)
                              return (
                                <Typography color="text.secondary">
                                  Select Template
                                </Typography>
                              );

                            return (
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1.5}
                              >
                                <Typography>{selectedOption.icon}</Typography>
                                <Typography fontWeight={500}>
                                  {selectedOption.label}
                                </Typography>
                              </Stack>
                            );
                          }}
                        >
                          {/* Dropdown Options */}
                          {[
                            { value: "0", label: "Simple IVR", icon: "📞" },
                            { value: "1", label: "DTMF Input", icon: "⌨️" },
                            { value: "2", label: "Call Patch", icon: "🔄" },
                          ].map((opt) => (
                            <MenuItem
                              key={opt.value}
                              value={opt.value}
                              sx={{ py: 1 }}
                            >
                              <Radio
                                checked={formData.templateId === opt.value}
                                sx={{ p: 0, mr: 2 }}
                              />
                              <Typography sx={{ mr: 1.5, fontSize: "1.2rem" }}>
                                {opt.icon}
                              </Typography>
                              <Typography>{opt.label}</Typography>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Paper>
                  </Grid>

                  {/* <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Location</InputLabel>
                      <Select
                        label="Location"
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                      >
                        <MenuItem value="Bangalore">Bangalore</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid> */}

                  {/* ── Batch Settings ─────────────────────────────────────── */}
                  <BatchingSettings
                    settings={batchSettings}
                    onChange={setBatchSettings}
                  />

                  {/* ── Contact Upload ─────────────────────────────────────── */}
                  {contactGroupId ? (
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          p: 1.5,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 2,
                          bgcolor: "background.default",
                        }}
                      >
                        <Stack spacing={1.5}>
                          {/* Top Info Row */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              flexWrap: "wrap",
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ display: "flex", alignItems: "center" }}
                            >
                              Using contact group:{" "}
                              <Box
                                component="span"
                                sx={{ fontWeight: "bold", mx: 0.5 }}
                              >
                                {searchParams.get("groupName")}
                              </Box>
                              {/* Loading / Count */}
                              {isCdLoading ? (
                                <Box
                                  sx={{
                                    display: "inline-block",
                                    width: 60,
                                    mx: 1,
                                  }}
                                >
                                  <LinearProgress />
                                </Box>
                              ) : contactGroupDetail?.contactsCount != null ? (
                                <Box
                                  component="span"
                                  sx={{ color: "text.secondary", ml: 0.5 }}
                                >
                                  (
                                  {contactGroupDetail.contactsCount.toLocaleString()}{" "}
                                  contacts)
                                </Box>
                              ) : null}
                            </Typography>
                          </Box>

                          {/* Interactive Configuration Row */}
                          {!isCdLoading &&
                            contactGroupDetail?.contactsCount &&
                            !contactGroupDetail?.isDb && (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 3,
                                  p: 1,
                                  px: 1.5,
                                  borderRadius: 1,
                                  bgcolor: "action.hover", // Creates a subtle grouping background
                                  flexWrap: "wrap",
                                }}
                              >
                                <FormControlLabel
                                  sx={{ m: 0 }}
                                  control={
                                    <Switch
                                      size="small"
                                      checked={useFullRange}
                                      onChange={(e) => {
                                        const isChecked = e.target.checked;
                                        setUseFullRange(isChecked);
                                        // If they turn off "Use full list", pre-populate the state with defaults
                                        if (!isChecked) {
                                          setFormData((prevData) => ({
                                            ...prevData,
                                            contactsGroupStartIndex:
                                              prevData.contactsGroupStartIndex ||
                                              "1",
                                            contactsGroupEndIndex:
                                              prevData.contactsGroupEndIndex ||
                                              contactGroupDetail?.contactsCount,
                                          }));
                                        }
                                      }}
                                      color="primary"
                                    />
                                  }
                                  label={
                                    <Typography variant="body2">
                                      Use full list
                                    </Typography>
                                  }
                                />

                                {/* Revealed Inputs */}
                                {!useFullRange && (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1.5,
                                    }}
                                  >
                                    <TextField
                                      size="small"
                                      type="number"
                                      name="contactsGroupStartIndex"
                                      placeholder="Start (e.g. 1)"
                                      helperText="Start (e.g. 1)"
                                      value={
                                        formData.contactsGroupStartIndex || "1"
                                      }
                                      onChange={handleChange}
                                      sx={{
                                        width: 160,
                                      }}
                                      inputProps={{
                                        min: 1,
                                        max:
                                          formData.contactsGroupEndIndex ||
                                          contactGroupDetail.contactsCount,
                                      }}
                                    />

                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      to
                                    </Typography>

                                    <TextField
                                      size="small"
                                      type="number"
                                      name="contactsGroupEndIndex"
                                      helperText={`End (Max ${contactGroupDetail.contactsCount})`}
                                      placeholder={`End (Max ${contactGroupDetail.contactsCount})`}
                                      value={
                                        formData.contactsGroupEndIndex ||
                                        contactGroupDetail.contactsCount
                                      }
                                      onChange={handleChange}
                                      sx={{
                                        width: 160,
                                      }}
                                      inputProps={{
                                        min:
                                          formData.contactsGroupStartIndex || 1,
                                        max: contactGroupDetail.contactsCount,
                                      }}
                                    />
                                  </Box>
                                )}
                              </Box>
                            )}
                          {!isCdLoading &&
                            contactGroupDetail?.contactsCount &&
                            contactGroupDetail?.isDb && (
                              <Box sx={{ mt: 2 }}>
                                <DatabaseAIAssistant
                                  key={contactGroupId}
                                  groupId={contactGroupDetail?.categoryId}
                                  columns={contactGroupDetail?.columns}
                                  onSuccess={(responseData) => {
                                    setAiDbFilter({
                                      contactGroupId,
                                      fullFilter: responseData.fullFilter,
                                      count: responseData.count,
                                    });
                                  }}
                                  onError={() => {}}
                                />
                              </Box>
                            )}
                        </Stack>
                      </Box>
                    </Grid>
                  ) : (
                    <Grid item xs={12}>
                      <Paper sx={{ overflow: "hidden" }}>
                        <Stack
                          direction="row"
                          spacing={2}
                          sx={{ p: 3, background: "#f8fafc" }}
                        >
                          <ContactPhone sx={{ color: "#3b82f6" }} />
                          <Box>
                            <Typography fontWeight={600}>
                              Use Contacts
                            </Typography>
                            {batchSettings.enabled && (
                              <Typography variant="caption" color="primary">
                                Batch mode: contacts will be sent inline (no
                                separate upload)
                              </Typography>
                            )}
                          </Box>
                        </Stack>

                        {!useContactsList ? (
                          <Box>
                            <Tabs
                              value={tabValue}
                              onChange={handleTabChange}
                              sx={{ px: 3 }}
                            >
                              {contactTabs.map((tab) => (
                                <Tab key={tab.value} label={tab.label} />
                              ))}
                            </Tabs>
                            <Box sx={{ p: 3 }}>
                              {tabValue === 0 && (
                                <>
                                  <TextField
                                    fullWidth
                                    multiline
                                    rows={6}
                                    name="contactList"
                                    placeholder={"9876543210\n9123456789"}
                                    value={formData.contactList}
                                    onChange={handleChange}
                                  />
                                  <Typography variant="body2" sx={{ mt: 1 }}>
                                    Total Numbers: {count}
                                  </Typography>
                                </>
                              )}
                              {tabValue === 1 && (
                                <Stack spacing={3}>
                                  <Box
                                    sx={{
                                      p: 6,
                                      border: "2px dashed #cbd5e1",
                                      textAlign: "center",
                                      borderRadius: 2,
                                      cursor: "pointer",
                                    }}
                                    onClick={() =>
                                      document
                                        .getElementById("file-upload")
                                        .click()
                                    }
                                  >
                                    <Upload
                                      sx={{
                                        fontSize: 60,
                                        color: "#3b82f6",
                                        mb: 2,
                                      }}
                                    />
                                    <input
                                      type="file"
                                      id="file-upload"
                                      name="newContactsFile"
                                      onChange={handleChange}
                                      accept=".csv,.txt"
                                      style={{ display: "none" }}
                                    />
                                    <Button variant="outlined" component="span">
                                      Choose .csv or .txt
                                    </Button>
                                    <Typography mt={2}>
                                      {formData.newContactsFile
                                        ? formData.newContactsFile.name
                                        : "10-digit numbers only"}
                                    </Typography>
                                  </Box>
                                  <TextField
                                    fullWidth
                                    required
                                    name="baseName"
                                    label="Base File Name"
                                    value={formData.baseName}
                                    onChange={handleChange}
                                  />
                                </Stack>
                              )}
                              {tabValue === 2 && (
                                <Autocomplete
                                  fullWidth
                                  options={oldBaseOptions}
                                  // Tell Autocomplete what text to display for each option
                                  getOptionLabel={(option) => option.label}
                                  // Autocomplete expects the whole object as the value.
                                  // We find the matching object based on your formData.oldBaseId
                                  value={
                                    oldBaseOptions.find(
                                      (opt) => opt.value === formData.oldBaseId
                                    ) || null
                                  }
                                  // When an option is selected, newValue is the selected object (or null if cleared)
                                  onChange={(event, newValue) => {
                                    // Construct a synthetic event so your existing handleChange still works
                                    handleChange({
                                      target: {
                                        name: "oldBaseId",
                                        value: newValue ? newValue.value : "", // Extract the value string/number
                                      },
                                    });
                                  }}
                                  // Render the actual input field
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label="Select Existing Contact File"
                                      required
                                      // Optional: If you want to keep the exact form layout feeling
                                      name="oldBaseId"
                                    />
                                  )}
                                />
                              )}
                            </Box>
                          </Box>
                        ) : (
                          <Box sx={{ p: 2 }} maxWidth={500}>
                            <FormControl fullWidth size="small">
                              <InputLabel id="group-select-label">
                                Select Group
                              </InputLabel>
                              <Select
                                labelId="group-select-label"
                                value={selectedGroup}
                                label="Select Contact List"
                                onChange={(e) =>
                                  setSelectedGroup(e.target.value)
                                }
                                disabled={isLoadingGroups}
                              >
                                {(groups || []).map((group) => (
                                  <MenuItem key={group} value={group}>
                                    {group}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            {isSchemaLoading ? (
                              <Box sx={{ p: 2, display: "flex", gap: 1 }}>
                                {[...Array(4)].map((_, i) => (
                                  <Skeleton
                                    key={i}
                                    variant="rounded"
                                    width={80}
                                    height={24}
                                  />
                                ))}
                              </Box>
                            ) : (
                              schemaData && (
                                <Box sx={{ p: 2 }}>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ mb: 1, display: "block" }}
                                  >
                                    Available Fields
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: 1,
                                    }}
                                  >
                                    {Object.keys(schemaData)
                                      .filter(
                                        (key) =>
                                          ![
                                            "_id",
                                            "groupName",
                                            "createdBy",
                                            "customerId",
                                            "createdOn",
                                            "updatedOn",
                                          ].includes(key)
                                      )
                                      .map((key) => (
                                        <Chip
                                          key={key}
                                          label={key}
                                          size="small"
                                          variant="outlined"
                                        />
                                      ))}
                                  </Box>
                                </Box>
                              )
                            )}
                          </Box>
                        )}
                      </Paper>
                    </Grid>
                  )}

                  {/* ── Audio Prompts ──────────────────────────────────────── */}
                  <Grid item xs={12}>
                    <Paper sx={{ p: 3, height: "100%" }}>
                      <Stack direction="row" spacing={1.5} mb={2.5}>
                        <AudioFile sx={{ color: "#3b82f6" }} />
                        <Typography fontWeight={600}>Voice Prompt</Typography>
                      </Stack>

                      {formData.templateId === "0" && (
                        <FormControl fullWidth>
                          <InputLabel>Welcome Prompt</InputLabel>
                          <Select
                            label="Welcome Prompt"
                            name="welcomePId"
                            value={formData.welcomePId}
                            onChange={handleChange}
                            required
                          >
                            {voices?.map((v) => (
                              <MenuItem
                                key={v.voiceFileId}
                                value={v.voiceFileId}
                              >
                                {v.originalFileName}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}

                      {(formData.templateId === "1" ||
                        formData.templateId === "2") && (
                        <>
                          <Grid container spacing={2} mb={3}>
                            <Grid item xs={12} md={4}>
                              <FormControl size="small" fullWidth>
                                <InputLabel>Menu Prompt</InputLabel>
                                <Select
                                  label="Menu Prompt"
                                  name="menuPId"
                                  value={formData.menuPId}
                                  onChange={handleChange}
                                  required
                                >
                                  {voices
                                    ?.filter(
                                      (item) => item.promptCategory === "menu"
                                    )
                                    .map((v) => (
                                      <MenuItem
                                        key={v.voiceFileId}
                                        value={v.voiceFileId}
                                      >
                                        {v.fileName}
                                      </MenuItem>
                                    ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <FormControl size="small" fullWidth>
                                <InputLabel>Menu Wait Time</InputLabel>
                                <Select
                                  label="Menu Wait Time"
                                  name="menuWaitTime"
                                  value={formData.menuWaitTime}
                                  onChange={handleChange}
                                >
                                  {[5, 10, 15, 20, 30].map((s) => (
                                    <MenuItem key={s} value={s.toString()}>
                                      {s} sec
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <FormControl size="small" fullWidth>
                                <InputLabel>RePrompt</InputLabel>
                                <Select
                                  label="RePrompt"
                                  name="rePrompt"
                                  value={formData.rePrompt}
                                  onChange={handleChange}
                                >
                                  {[0, 1, 2, 3].map((num) => (
                                    <MenuItem key={num} value={num.toString()}>
                                      {num}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                          </Grid>

                          <Divider sx={{ mb: 2 }} />
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                          >
                            Optional Prompts
                          </Typography>

                          <Grid container spacing={2}>
                            {[
                              {
                                label: "Welcome Prompt",
                                name: "welcomePId",
                                category: "welcome",
                              },
                              {
                                label: "No Input Prompt",
                                name: "noInputPId",
                                category: "noinput",
                              },
                              {
                                label: "Wrong Input Prompt",
                                name: "wrongInputPId",
                                category: "wronginput",
                              },
                              {
                                label: "Thanks Prompt",
                                name: "thanksPId",
                                category: "thanks",
                              },
                            ].map((item) => (
                              <Grid item xs={12} md={6} key={item.name}>
                                <Box
                                  sx={{
                                    border: "1px solid",
                                    borderColor: enabledPrompts[item.name]
                                      ? "primary.light"
                                      : "divider",
                                    borderRadius: 2,
                                    p: 1.5,
                                  }}
                                >
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={enabledPrompts[item.name]}
                                        onChange={(e) => {
                                          setEnabledPrompts((prev) => ({
                                            ...prev,
                                            [item.name]: e.target.checked,
                                          }));
                                          if (!e.target.checked)
                                            setFormData((prev) => ({
                                              ...prev,
                                              [item.name]: "",
                                            }));
                                        }}
                                      />
                                    }
                                    label={
                                      <Typography
                                        variant="body2"
                                        fontWeight={500}
                                      >
                                        {item.label}
                                      </Typography>
                                    }
                                  />
                                  {enabledPrompts[item.name] && (
                                    <Select
                                      name={item.name}
                                      value={formData[item.name]}
                                      onChange={handleChange}
                                      displayEmpty
                                      fullWidth
                                      size="small"
                                      sx={{ mt: 1 }}
                                    >
                                      <MenuItem value="">
                                        <em>Select File</em>
                                      </MenuItem>
                                      {voices
                                        ?.filter(
                                          (v) =>
                                            v.promptCategory === item.category
                                        )
                                        .map((v) => (
                                          <MenuItem
                                            key={v.voiceFileId}
                                            value={v.voiceFileId}
                                          >
                                            {v.fileName}
                                          </MenuItem>
                                        ))}
                                    </Select>
                                  )}
                                </Box>
                              </Grid>
                            ))}
                          </Grid>
                        </>
                      )}
                    </Paper>
                  </Grid>

                  {/* ── Advanced Options ───────────────────────────────────── */}
                  <Grid item xs={12}>
                    <Paper sx={{ p: 3 }}>
                      <Stack direction="row" spacing={1.5} mb={3}>
                        <Settings sx={{ color: "#3b82f6" }} />
                        <Typography fontWeight={600}>
                          Advanced Options
                        </Typography>
                      </Stack>

                      {formData.templateId === "1" && (
                        <Grid container spacing={3}>
                          <Grid item xs={12} md={6}>
                            <TextField
                              select
                              size="small"
                              fullWidth
                              label="DTMF input"
                              name="dtmf"
                              value={formData.dtmf}
                              onChange={handleChange}
                            >
                              {[...Array(10).keys()].map((num) => (
                                <MenuItem key={num} value={num}>
                                  {num}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>
                        </Grid>
                      )}

                      {formData.templateId === "2" && (
                        <Grid container spacing={3}>
                          <Grid item xs={12}>
                            <Typography variant="subtitle2" gutterBottom>
                              Agent Groups &amp; DTMF Mapping
                            </Typography>
                            {loadingGroups ? (
                              <CircularProgress size={24} />
                            ) : (
                              <Stack spacing={2}>
                                {selectedAgentGroups.map((group, index) => (
                                  <Paper
                                    key={index}
                                    sx={{ p: 2, bgcolor: "#fafafa" }}
                                  >
                                    <Grid
                                      container
                                      spacing={2}
                                      alignItems="center"
                                    >
                                      <Grid item xs={5}>
                                        <FormControl fullWidth size="small">
                                          <InputLabel>Agent Group</InputLabel>
                                          <Select
                                            label="Agent Group"
                                            value={group.groupId || ""}
                                            onChange={(e) => {
                                              const ng = [
                                                ...selectedAgentGroups,
                                              ];
                                              const sel = getGroupById(
                                                e.target.value
                                              );
                                              ng[index] = {
                                                ...ng[index],
                                                groupId: e.target.value,
                                                groupName: sel?.groupName || "",
                                              };
                                              setSelectedAgentGroups(ng);
                                            }}
                                          >
                                            {agentGroups?.map((g) => (
                                              <MenuItem
                                                key={g.groupId}
                                                value={g.groupId}
                                              >
                                                {g.groupName} (
                                                {g.agents?.length || 0} agents)
                                              </MenuItem>
                                            ))}
                                          </Select>
                                        </FormControl>
                                      </Grid>
                                      <Grid item xs={1}>
                                        <IconButton
                                          color="primary"
                                          size="small"
                                          onClick={() => {
                                            const gd = getGroupById(
                                              group.groupId
                                            );
                                            if (gd) {
                                              setViewingGroup(gd);
                                              setViewGroupDialogOpen(true);
                                            }
                                          }}
                                          disabled={!group.groupId}
                                        >
                                          <Info />
                                        </IconButton>
                                      </Grid>
                                      <Grid item xs={4}>
                                        <TextField
                                          fullWidth
                                          size="small"
                                          label="DTMF Digit"
                                          value={group.dtmf || ""}
                                          onChange={(e) => {
                                            const ng = [...selectedAgentGroups];
                                            ng[index].dtmf = e.target.value;
                                            setSelectedAgentGroups(ng);
                                          }}
                                          placeholder="e.g., 1"
                                        />
                                      </Grid>
                                      <Grid item xs={2}>
                                        <IconButton
                                          color="error"
                                          onClick={() =>
                                            setSelectedAgentGroups(
                                              selectedAgentGroups.filter(
                                                (_, i) => i !== index
                                              )
                                            )
                                          }
                                        >
                                          <Close />
                                        </IconButton>
                                      </Grid>
                                    </Grid>
                                  </Paper>
                                ))}
                                <Stack direction="row" spacing={2}>
                                  <Button
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    onClick={() =>
                                      setSelectedAgentGroups([
                                        ...selectedAgentGroups,
                                        {
                                          groupId: "",
                                          groupName: "",
                                          dtmf: "",
                                        },
                                      ])
                                    }
                                  >
                                    Add Agent Group
                                  </Button>
                                  <Button
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    onClick={() => setCreateGroupDialog(true)}
                                  >
                                    Create New Group
                                  </Button>
                                </Stack>
                              </Stack>
                            )}
                          </Grid>
                        </Grid>
                      )}

                      {formData.templateId === "0" && (
                        <Typography color="text.secondary">
                          Simple IVR — no extra settings needed
                        </Typography>
                      )}
                    </Paper>
                  </Grid>

                  {/* ── Retry Settings ─────────────────────────────────────── */}
                  <Grid item xs={12}>
                    <Paper sx={{ p: 3 }}>
                      <Stack direction="row" spacing={1.5} mb={2.5}>
                        <Schedule sx={{ color: "#3b82f6" }} />
                        <Typography fontWeight={600}>Retry Settings</Typography>
                      </Stack>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <FormControl size="small" fullWidth>
                            <InputLabel>Attempts</InputLabel>
                            <Select
                              label="Attempts"
                              name="retries"
                              value={formData.retries}
                              onChange={handleChange}
                            >
                              {[0, 1, 2].map((n) => (
                                <MenuItem key={n} value={n.toString()}>
                                  {n}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                          <FormControl size="small" fullWidth>
                            <InputLabel>Interval</InputLabel>
                            <Select
                              label="Interval"
                              name="retryInterval"
                              value={formData.retryInterval}
                              onChange={handleChange}
                            >
                              <MenuItem value="900">15 min</MenuItem>
                              <MenuItem value="1800">30 min</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>

                      {/* OVERLAP WARNING ALERT */}
                      {retryOverlapWarning && (
                        <Box sx={{ mt: 3 }}>
                          <Alert severity="warning">
                            <strong>Heads up:</strong> Your current retry
                            settings ({formData.retries} attempts spaced by{" "}
                            {parseInt(formData.retryInterval) / 60} mins) might
                            cause retries from upcoming batches to overlap with
                            your configured{" "}
                            <strong>Non-Active (Blackout) Hours</strong>.
                          </Alert>
                        </Box>
                      )}
                    </Paper>
                  </Grid>

                  {/* ── Notifications ──────────────────────────────────────── */}
                  <Grid item xs={12}>
                    <Paper sx={{ p: 3 }}>
                      <Stack direction="row" spacing={1.5} mb={3}>
                        <Sms sx={{ color: "#3b82f6" }} />
                        <Typography fontWeight={600}>Notifications</Typography>
                      </Stack>
                      <Grid container spacing={3}>
                        {[
                          ["sendSmsSuccess", "SMS Success"],
                          ["sendSmsFail", "SMS Failure"],
                          ["sendLater", "Schedule"],
                        ].map(([name, label]) => (
                          <Grid item xs={12} sm={6} md={3} key={name}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  name={name}
                                  checked={formData[name]}
                                  onChange={handleChange}
                                />
                              }
                              label={label}
                            />
                          </Grid>
                        ))}
                        {formData.sendLater && (
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              type="datetime-local"
                              name="scheduleTime"
                              label="Schedule Time"
                              value={formData.scheduleTime}
                              onChange={handleChange}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                        )}
                      </Grid>
                    </Paper>
                  </Grid>

                  {/* ── Executive Notifications ───────────────────────────── */}
                  <Grid item xs={12}>
                    <Paper
                      sx={{
                        overflow: "hidden",
                        border: executiveSettings
                          ? "1px solid rgba(59,130,246,0.4)"
                          : "1px solid #e5e7eb",
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ ...sectionHeaderSx }}
                        // onClick={() => setExecutiveSettings((prev) => !prev)}
                      >
                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems="center"
                        >
                          <Schedule sx={{ color: "#3b82f6" }} />
                          <Box>
                            <Typography fontWeight={600}>
                              Executive Notifications Enabled always
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Executive will be notified of the customers to
                              call back
                            </Typography>
                          </Box>
                        </Stack>
                        <Switch
                          disabled
                          checked={executiveSettings}
                          onChange={(e) => {
                            e.stopPropagation();
                            setExecutiveSettings(e.target.checked);
                          }}
                          color="primary"
                        />
                      </Stack>
                    </Paper>
                  </Grid>

                  {/* ── Failed Messages Fallback ──────────────────────────── */}
                  <Grid item xs={12}>
                    <Paper
                      sx={{
                        overflow: "hidden",
                        border: failedMessagesFallback
                          ? "1px solid rgba(59,130,246,0.4)"
                          : "1px solid #e5e7eb",
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ ...sectionHeaderSx, cursor: "pointer" }}
                      >
                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems="center"
                        >
                          <Message sx={{ color: "#3b82f6" }} />
                          <Box>
                            <Typography fontWeight={600}>
                              Enable Failed Messages Fallback
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Failed calls will receive a follow-up via WhatsApp
                              or RCS
                            </Typography>
                          </Box>
                        </Stack>
                        <Tooltip
                          title={
                            !contactGroupId
                              ? "Failed message fallback is available only for contact group campaigns."
                              : ""
                          }
                          arrow
                          placement="top"
                        >
                          <span>
                            <Switch
                              disabled={!contactGroupId}
                              checked={failedMessagesFallback}
                              onChange={(e) => {
                                e.stopPropagation();
                                setFailedMessagesFallback(e.target.checked);
                                if (!e.target.checked) {
                                  setFailedMsgChannel("whatsapp");
                                  setFailedMsgTemplateId("");
                                }
                              }}
                              color="primary"
                            />
                          </span>
                        </Tooltip>
                      </Stack>

                      {failedMessagesFallback && (
                        <Box sx={{ p: 3 }}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            mb={3}
                          >
                            Choose the channel and template to send when a call
                            fails or goes unanswered.
                          </Typography>

                          <Grid container spacing={3} alignItems="flex-start">
                            <Grid item xs={12} md={4}>
                              <Typography
                                variant="subtitle2"
                                fontWeight={600}
                                mb={1.5}
                              >
                                Channel
                              </Typography>
                              <Stack direction="row" spacing={1.5}>
                                {[
                                  {
                                    value: "whatsapp",
                                    label: "WhatsApp",
                                    icon: (
                                      <WhatsApp
                                        sx={{ fontSize: 20, color: "#25d366" }}
                                      />
                                    ),
                                    activeBg: "#f0fdf4",
                                    activeBorder: "#25d366",
                                  },
                                  {
                                    value: "rcs",
                                    label: "RCS",
                                    icon: (
                                      <Message
                                        sx={{ fontSize: 20, color: "#6366f1" }}
                                      />
                                    ),
                                    activeBg: "#f5f3ff",
                                    activeBorder: "#6366f1",
                                  },
                                ].map((ch) => (
                                  <Paper
                                    key={ch.value}
                                    variant="outlined"
                                    onClick={() => {
                                      setFailedMsgChannel(ch.value);
                                      setFailedMsgTemplateId("");
                                    }}
                                    sx={{
                                      flex: 1,
                                      p: 2,
                                      cursor: "pointer",
                                      textAlign: "center",
                                      borderRadius: 2,
                                      transition: "all 0.15s",
                                      bgcolor:
                                        failedMsgChannel === ch.value
                                          ? ch.activeBg
                                          : "#fafafa",
                                      borderColor:
                                        failedMsgChannel === ch.value
                                          ? ch.activeBorder
                                          : "#e5e7eb",
                                      borderWidth:
                                        failedMsgChannel === ch.value ? 2 : 1,
                                    }}
                                  >
                                    <Stack alignItems="center" spacing={0.5}>
                                      {ch.icon}
                                      <Typography
                                        variant="body2"
                                        fontWeight={
                                          failedMsgChannel === ch.value
                                            ? 700
                                            : 400
                                        }
                                      >
                                        {ch.label}
                                      </Typography>
                                    </Stack>
                                  </Paper>
                                ))}
                              </Stack>
                            </Grid>

                            <Grid item xs={12} md={8}>
                              <Typography
                                variant="subtitle2"
                                fontWeight={600}
                                mb={1.5}
                              >
                                {failedMsgChannel === "whatsapp"
                                  ? "WhatsApp Campaign"
                                  : "RCS Campaign"}
                              </Typography>
                              <FormControl fullWidth size="small">
                                <InputLabel>
                                  Select{" "}
                                  {failedMsgChannel === "whatsapp"
                                    ? "WhatsApp"
                                    : "RCS"}{" "}
                                  Campaign
                                </InputLabel>
                                <Select
                                  label={`Select ${
                                    failedMsgChannel === "whatsapp"
                                      ? "WhatsApp"
                                      : "RCS"
                                  } Campaign`}
                                  value={failedMsgTemplateId}
                                  onChange={(e) =>
                                    setFailedMsgTemplateId(e.target.value)
                                  }
                                >
                                  <MenuItem value="">
                                    <em>Select a campaign</em>
                                  </MenuItem>
                                  {failedMsgTemplates.map((t) => (
                                    <MenuItem
                                      key={t.campaignName}
                                      value={t.campaignName}
                                    >
                                      <Stack>
                                        <Typography
                                          variant="body2"
                                          fontWeight={500}
                                        >
                                          {t.campaignName}
                                        </Typography>
                                        {t.templateId && (
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                          >
                                            ID: {t.templateId}
                                          </Typography>
                                        )}
                                      </Stack>
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>

                              {failedMsgTemplateId && (
                                <Stack direction="row" spacing={1} mt={1.5}>
                                  <Chip
                                    size="small"
                                    icon={
                                      failedMsgChannel === "whatsapp" ? (
                                        <WhatsApp
                                          sx={{
                                            fontSize: "14px !important",
                                            color: "#25d366 !important",
                                          }}
                                        />
                                      ) : (
                                        <Message
                                          sx={{
                                            fontSize: "14px !important",
                                            color: "#6366f1 !important",
                                          }}
                                        />
                                      )
                                    }
                                    label={
                                      failedMsgChannel === "whatsapp"
                                        ? "WhatsApp"
                                        : "RCS"
                                    }
                                    sx={{
                                      bgcolor:
                                        failedMsgChannel === "whatsapp"
                                          ? "#dcfce7"
                                          : "#ede9fe",
                                      color:
                                        failedMsgChannel === "whatsapp"
                                          ? "#166534"
                                          : "#5b21b6",
                                    }}
                                  />
                                  <Chip
                                    size="small"
                                    label={failedMsgTemplateId}
                                    variant="outlined"
                                    color="primary"
                                  />
                                </Stack>
                              )}

                              {failedMsgTemplates.length === 0 && (
                                <Alert severity="warning" sx={{ mt: 2 }}>
                                  No{" "}
                                  {failedMsgChannel === "whatsapp"
                                    ? "WhatsApp"
                                    : "RCS"}{" "}
                                  campaigns found in this contact group. Please
                                  ensure the group has associated campaigns.
                                </Alert>
                              )}
                            </Grid>
                          </Grid>

                          <Alert severity="info" sx={{ mt: 3 }}>
                            The selected campaign will be triggered
                            automatically for contacts whose calls fail or are
                            unanswered.
                          </Alert>
                        </Box>
                      )}
                    </Paper>
                  </Grid>

                  {/* ── Fallback Message Settings ─────────────────────────── */}
                  <Grid item xs={12}>
                    <Paper
                      sx={{
                        overflow: "hidden",
                        border: fallbackEnabled
                          ? "1px solid rgba(59,130,246,0.4)"
                          : "1px solid #e5e7eb",
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ ...sectionHeaderSx, cursor: "pointer" }}
                      >
                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems="center"
                        >
                          <Tune sx={{ color: "#3b82f6" }} />
                          <Box>
                            <Typography fontWeight={600}>
                              Fallback Message Settings
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Send WhatsApp / RCS messages based on call
                              duration or DTMF input
                            </Typography>
                          </Box>
                        </Stack>
                        <Tooltip
                          title={
                            !contactGroupId
                              ? "This option is available only for contact group campaigns."
                              : ""
                          }
                          arrow
                          placement="top"
                        >
                          <span>
                            <Switch
                              disabled={!contactGroupId}
                              checked={fallbackEnabled}
                              onChange={(e) => {
                                e.stopPropagation();
                                setFallbackEnabled(e.target.checked);
                              }}
                              color="primary"
                            />
                          </span>
                        </Tooltip>
                      </Stack>

                      {fallbackEnabled && (
                        <Box sx={{ p: 3 }}>
                          {/* ── Duration-Based Fallback header ── */}
                          <Typography
                            variant="subtitle1"
                            fontWeight={600}
                            mb={0.5}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Schedule
                              fontSize="small"
                              sx={{ color: "#6366f1" }}
                            />
                            Duration-Based Fallback
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            mb={3}
                          >
                            Send a message when call duration falls within a
                            specified range (seconds). You can add multiple
                            conditions per channel.
                          </Typography>

                          <Grid container spacing={3} mb={4}>
                            {/* WhatsApp range rows */}
                            <Grid item xs={12}>
                              {renderRangeRows({
                                state: rangeWa,
                                setter: setRangeWa,
                                campaigns: wabaCampaigns,
                                label: "WhatsApp Fallback",
                                bgColor: "#f0f9ff",
                                borderColor: "#7dd3fc",
                                channelIcon: (
                                  <WhatsApp
                                    sx={{ color: "#25d366", fontSize: 22 }}
                                  />
                                ),
                              })}
                            </Grid>

                            {/* RCS range rows */}
                            <Grid item xs={12}>
                              {renderRangeRows({
                                state: rangeRcs,
                                setter: setRangeRcs,
                                campaigns: rcsCampaigns,
                                label: "RCS Fallback",
                                bgColor: "#f5f3ff",
                                borderColor: "#c4b5fd",
                                channelIcon: (
                                  <Message
                                    sx={{ color: "#6366f1", fontSize: 22 }}
                                  />
                                ),
                              })}
                            </Grid>
                          </Grid>

                          <Divider sx={{ my: 3 }} />

                          {/* ── DTMF-Based Fallback ── */}
                          <Typography
                            variant="subtitle1"
                            fontWeight={600}
                            mb={0.5}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Sms fontSize="small" sx={{ color: "#f59e0b" }} />
                            DTMF-Based Fallback
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            mb={3}
                          >
                            Send a message when the caller presses a specific
                            key.
                          </Typography>

                          <Stack spacing={2}>
                            {dtmfRows.map((row, idx) => (
                              <Paper
                                key={idx}
                                variant="outlined"
                                sx={{
                                  p: 2.5,
                                  borderRadius: 2,
                                  bgcolor:
                                    row.type === "whatsapp"
                                      ? "#f0fdf4"
                                      : "#f5f3ff",
                                  borderColor:
                                    row.type === "whatsapp"
                                      ? "#86efac"
                                      : "#c4b5fd",
                                }}
                              >
                                <Grid
                                  container
                                  spacing={2}
                                  alignItems="flex-start"
                                >
                                  <Grid item xs={6} sm={2}>
                                    <FormControl fullWidth size="small">
                                      <InputLabel>Key</InputLabel>
                                      <Select
                                        label="Key"
                                        value={row.input}
                                        onChange={(e) =>
                                          updateDtmfRow(
                                            idx,
                                            "input",
                                            e.target.value
                                          )
                                        }
                                      >
                                        {DTMF_INPUTS.map((k) => (
                                          <MenuItem key={k} value={k}>
                                            {k}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  </Grid>
                                  <Grid item xs={6} sm={2}>
                                    <FormControl fullWidth size="small">
                                      <InputLabel>Channel</InputLabel>
                                      <Select
                                        label="Channel"
                                        value={row.type}
                                        onChange={(e) =>
                                          updateDtmfRow(
                                            idx,
                                            "type",
                                            e.target.value
                                          )
                                        }
                                      >
                                        <MenuItem value="whatsapp">
                                          <Stack
                                            direction="row"
                                            alignItems="center"
                                            spacing={1}
                                          >
                                            <WhatsApp
                                              sx={{
                                                fontSize: 16,
                                                color: "#25d366",
                                              }}
                                            />
                                            <span>WhatsApp</span>
                                          </Stack>
                                        </MenuItem>
                                        <MenuItem value="rcs">
                                          <Stack
                                            direction="row"
                                            alignItems="center"
                                            spacing={1}
                                          >
                                            <Message
                                              sx={{
                                                fontSize: 16,
                                                color: "#6366f1",
                                              }}
                                            />
                                            <span>RCS</span>
                                          </Stack>
                                        </MenuItem>
                                      </Select>
                                    </FormControl>
                                  </Grid>
                                  <Grid item xs={12} sm={3}>
                                    <FormControl fullWidth size="small">
                                      <InputLabel>Campaign</InputLabel>
                                      <Select
                                        label="Campaign"
                                        value={row.templateId}
                                        onChange={(e) =>
                                          updateDtmfRow(
                                            idx,
                                            "templateId",
                                            e.target.value
                                          )
                                        }
                                      >
                                        <MenuItem value="">
                                          <em>Select</em>
                                        </MenuItem>
                                        {row.type === "whatsapp"
                                          ? wabaCampaigns.map((t) => (
                                              <MenuItem
                                                key={t.campaignName}
                                                value={t.campaignName}
                                              >
                                                {t.campaignName}
                                                <Typography
                                                  component="span"
                                                  variant="caption"
                                                  color="text.secondary"
                                                  ml={1}
                                                >
                                                  ({t.templateId})
                                                </Typography>
                                              </MenuItem>
                                            ))
                                          : rcsCampaigns.map((t) => (
                                              <MenuItem
                                                key={t.campaignName}
                                                value={t.campaignName}
                                              >
                                                {t.campaignName}
                                              </MenuItem>
                                            ))}
                                      </Select>
                                    </FormControl>
                                  </Grid>
                                  <Grid item xs={12} sm={3}>
                                    <FormControl fullWidth size="small">
                                      <InputLabel>Condition</InputLabel>
                                      <Select
                                        label="Condition"
                                        value={row.condition}
                                        onChange={(e) =>
                                          updateDtmfRow(
                                            idx,
                                            "condition",
                                            e.target.value
                                          )
                                        }
                                      >
                                        <MenuItem value="">
                                          <em>Any outcome</em>
                                        </MenuItem>
                                        {WA_CONDITIONS.map((c) => (
                                          <MenuItem key={c} value={c}>
                                            {c.charAt(0).toUpperCase() +
                                              c.slice(1)}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  </Grid>
                                  <Grid
                                    item
                                    xs={12}
                                    sm={2}
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "flex-end",
                                    }}
                                  >
                                    <IconButton
                                      color="error"
                                      size="small"
                                      onClick={() => removeDtmfRow(idx)}
                                      disabled={dtmfRows.length === 1}
                                    >
                                      <DeleteOutlineIcon />
                                    </IconButton>
                                  </Grid>
                                </Grid>
                                {row.templateId && (
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    mt={1.5}
                                    flexWrap="wrap"
                                  >
                                    <Chip
                                      size="small"
                                      label={`Key: ${row.input}`}
                                      color="warning"
                                    />
                                    <Chip
                                      size="small"
                                      icon={
                                        row.type === "whatsapp" ? (
                                          <WhatsApp
                                            sx={{ fontSize: "14px !important" }}
                                          />
                                        ) : (
                                          <Message
                                            sx={{ fontSize: "14px !important" }}
                                          />
                                        )
                                      }
                                      label={row.templateId}
                                      sx={{
                                        bgcolor:
                                          row.type === "whatsapp"
                                            ? "#dcfce7"
                                            : "#ede9fe",
                                        color:
                                          row.type === "whatsapp"
                                            ? "#166534"
                                            : "#5b21b6",
                                      }}
                                    />
                                    {row.condition && (
                                      <Chip
                                        size="small"
                                        label={`If: ${row.condition}`}
                                        color="default"
                                      />
                                    )}
                                  </Stack>
                                )}
                              </Paper>
                            ))}
                            <Button
                              variant="outlined"
                              startIcon={<AddIcon />}
                              onClick={addDtmfRow}
                              sx={{ alignSelf: "flex-start" }}
                            >
                              Add DTMF Rule
                            </Button>
                          </Stack>
                        </Box>
                      )}
                    </Paper>
                  </Grid>

                  {/* ── Submit ─────────────────────────────────────────────── */}
                  <Grid item xs={12}>
                    <Box sx={{ textAlign: "center", mt: 4 }}>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={isSubmitting || isUploadingContacts}
                        sx={{
                          px: 8,
                          py: 1.8,
                          fontSize: "1.1rem",
                          background:
                            "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
                          "&:hover": {
                            background:
                              "linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)",
                          },
                        }}
                        startIcon={
                          isSubmitting ? null : batchSettings.enabled ? (
                            <Preview />
                          ) : (
                            <Campaign />
                          )
                        }
                      >
                        {isSubmitting || isUploadingContacts ? (
                          <CircularProgress size={24} sx={{ color: "#fff" }} />
                        ) : batchSettings.enabled ? (
                          "📋 Preview Batch Schedule"
                        ) : (
                          "🚀 Launch Campaign Now"
                        )}
                      </Button>
                      {batchSettings.enabled && (
                        <Typography
                          variant="caption"
                          display="block"
                          color="text.secondary"
                          mt={1}
                        >
                          Contacts will be sent inline — no separate upload
                          needed
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </form>
            </CardContent>
          </Card>
        </Grow>
      </Box>

      {/* ── BATCH PREVIEW DIALOG ──────────────────────────────────────────────── */}
      <Dialog
        open={previewOpen}
        onClose={() => !isSubmitting && setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Schedule color="primary" />
            <Typography variant="h6" fontWeight={700}>
              Batch Schedule Preview
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {previewData && (
            <>
              <Stack direction="row" spacing={1.5} flexWrap="wrap" mb={3}>
                <Chip
                  icon={<ContactPhone />}
                  label={`${previewData.totalContacts.toLocaleString()} total contacts`}
                  color="primary"
                />
                <Chip
                  icon={<Campaign />}
                  label={`${previewData.totalBatches} batches`}
                  color="secondary"
                />
                <Chip
                  icon={<Schedule />}
                  label={`${previewData.batchSize.toLocaleString()} per batch`}
                />
                <Chip
                  icon={<AccessTime />}
                  label={`${previewData.intervalMinutes} min interval`}
                />
              </Stack>

              {batchSettings.nonActiveHours.length > 0 && (
                <Alert severity="warning" icon={<Warning />} sx={{ mb: 2 }}>
                  <strong>Blackout windows applied:</strong>{" "}
                  {batchSettings.nonActiveHours.map((w, i) => (
                    <Chip
                      key={i}
                      size="small"
                      label={`${w.start} – ${w.end}`}
                      sx={{ ml: 0.5 }}
                    />
                  ))}{" "}
                  — batch times automatically skip these windows.
                </Alert>
              )}

              <Alert severity="info" sx={{ mb: 3 }}>
                <strong>Active days:</strong>{" "}
                {batchSettings.activeDays
                  .sort()
                  .map((d) => DAY_LABELS[d])
                  .join(", ")}
              </Alert>

              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Exact launch times (IST):
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  maxHeight: 420,
                  overflow: "auto",
                  bgcolor: "#fafafa",
                  borderRadius: 2,
                }}
              >
                <Stack spacing={0}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "80px 1fr 1fr",
                      px: 2,
                      py: 1,
                      bgcolor: "#f1f5f9",
                      borderBottom: "1px solid #e2e8f0",
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color="text.secondary"
                    >
                      BATCH
                    </Typography>
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color="text.secondary"
                    >
                      CONTACTS
                    </Typography>
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color="text.secondary"
                    >
                      SCHEDULED AT (IST)
                    </Typography>
                  </Box>
                  {previewData.schedules.map((sch, i) => (
                    <Box
                      key={i}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "80px 1fr 1fr",
                        px: 2,
                        py: 1.5,
                        bgcolor: i % 2 === 0 ? "#fff" : "#f8fafc",
                        borderBottom: "1px solid #f1f5f9",
                        "&:last-child": { borderBottom: "none" },
                        "&:hover": { bgcolor: "#eff6ff" },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Chip
                          size="small"
                          label={`#${sch.batch}`}
                          color="primary"
                          variant={i === 0 ? "filled" : "outlined"}
                        />
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{ display: "flex", alignItems: "center" }}
                      >
                        {sch.contacts.toLocaleString()}
                        {i === 0 && (
                          <Chip
                            size="small"
                            label="First"
                            color="success"
                            sx={{ ml: 1, height: 18, fontSize: "0.65rem" }}
                          />
                        )}
                        {i === previewData.schedules.length - 1 && (
                          <Chip
                            size="small"
                            label="Last"
                            color="warning"
                            sx={{ ml: 1, height: 18, fontSize: "0.65rem" }}
                          />
                        )}
                      </Typography>
                      <Typography
                        variant="body2"
                        fontFamily="monospace"
                        fontWeight={600}
                      >
                        {sch.scheduledAt}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Paper>

              <Alert severity="success" sx={{ mt: 2 }}>
                Campaign will finish around{" "}
                <strong>
                  {
                    previewData.schedules[previewData.schedules.length - 1]
                      ?.scheduledAt
                  }
                </strong>
                . Contacts are sent inline — no separate file upload.
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button onClick={() => setPreviewOpen(false)} disabled={isSubmitting}>
            ← Go Back &amp; Edit
          </Button>
          <Button
            variant="contained"
            disabled={isSubmitting}
            onClick={handlePreviewConfirm}
            startIcon={
              isSubmitting ? (
                <CircularProgress size={18} sx={{ color: "#fff" }} />
              ) : (
                <Campaign />
              )
            }
            sx={{
              px: 4,
              background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
              },
            }}
          >
            {isSubmitting ? "Launching…" : "✅ Confirm & Launch Campaign"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── SUCCESS DIALOG ──────────────────────────────────────────────────── */}
      <Dialog
        open={successDialog.open}
        onClose={() =>
          setSuccessDialog({ open: false, campaignId: "", message: "" })
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: "center", pt: 4 }}>
          <SuccessIcon sx={{ fontSize: 80, color: "#10b981", mb: 2 }} />
          <Typography variant="h4" fontWeight={700} color="#10b981">
            Campaign Launched!
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center", py: 3 }}>
          <Typography variant="h6" gutterBottom>
            {successDialog.message}
          </Typography>
          <Box
            sx={{
              mt: 3,
              p: 3,
              background: "#f0fdf4",
              borderRadius: 3,
              display: "inline-block",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              CAMPAIGN ID
            </Typography>
            <Typography
              variant="h4"
              fontWeight={700}
              sx={{ fontFamily: "monospace", letterSpacing: 2 }}
            >
              {successDialog.campaignId}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 4 }}>
          <Button
            variant="contained"
            onClick={() => {
              setSuccessDialog({ open: false, campaignId: "", message: "" });
              window.location.reload();
            }}
            sx={{ px: 6, py: 1.5 }}
          >
            Create Another Campaign
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── ERROR DIALOG ────────────────────────────────────────────────────── */}
      <Dialog
        open={errorDialog.open}
        onClose={() => setErrorDialog({ open: false, message: "" })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: "center", pt: 4, pb: 1 }}>
          <Box
            sx={{
              display: "inline-flex",
              p: 2,
              borderRadius: "50%",
              bgcolor: "#fef2f2",
              mb: 2,
            }}
          >
            <ErrorOutline sx={{ fontSize: 56, color: "#ef4444" }} />
          </Box>
          <Typography variant="h5" fontWeight={700} color="#dc2626">
            Launch Failed
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center", pb: 1 }}>
          <Paper
            sx={{
              p: 3,
              bgcolor: "#fff5f5",
              border: "1px solid #fecaca",
              borderRadius: 2,
              mt: 1,
            }}
          >
            <Typography
              variant="body1"
              color="#7f1d1d"
              sx={{ lineHeight: 1.7 }}
            >
              {errorDialog.message}
            </Typography>
          </Paper>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 2 }}
          >
            Please contact your account manager for renewal process
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3, gap: 1.5 }}>
          <Button
            variant="outlined"
            color="error"
            onClick={() => setErrorDialog({ open: false, message: "" })}
            sx={{ px: 4 }}
          >
            Dismiss
          </Button>
          <Button
            variant="contained"
            onClick={() => setErrorDialog({ open: false, message: "" })}
            sx={{
              px: 4,
              bgcolor: "#ef4444",
              "&:hover": { bgcolor: "#dc2626" },
            }}
          >
            Try Again
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── CREATE AGENT GROUP DIALOG ────────────────────────────────────────── */}
      <Dialog
        open={createGroupDialog}
        onClose={() => !isCreatingGroup && setCreateGroupDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create New Agent Group</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="Group Name"
              fullWidth
              required
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
            />
            <Typography variant="subtitle2">Agents</Typography>
            {newGroupAgents.map((agent, idx) => (
              <Paper key={idx} sx={{ p: 2, bgcolor: "#f9f9f9" }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={5}>
                    <TextField
                      label="Agent Number"
                      fullWidth
                      size="small"
                      required
                      value={agent.agentNumber}
                      onChange={(e) => {
                        const u = [...newGroupAgents];
                        u[idx].agentNumber = e.target.value;
                        setNewGroupAgents(u);
                      }}
                      inputProps={{ maxLength: 10 }}
                      helperText="10-digit number"
                    />
                  </Grid>
                  <Grid item xs={5}>
                    <TextField
                      label="Agent Name"
                      fullWidth
                      size="small"
                      value={agent.agentName}
                      onChange={(e) => {
                        const u = [...newGroupAgents];
                        u[idx].agentName = e.target.value;
                        setNewGroupAgents(u);
                      }}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <IconButton
                      color="error"
                      onClick={() =>
                        setNewGroupAgents(
                          newGroupAgents.filter((_, i) => i !== idx)
                        )
                      }
                      disabled={newGroupAgents.length === 1}
                    >
                      <Close />
                    </IconButton>
                  </Grid>
                </Grid>
              </Paper>
            ))}
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() =>
                setNewGroupAgents([
                  ...newGroupAgents,
                  { agentNumber: "", agentName: "" },
                ])
              }
            >
              Add Another Agent
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCreateGroupDialog(false)}
            disabled={isCreatingGroup}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={
              !newGroupName.trim() ||
              newGroupAgents.some((a) => !a.agentNumber.trim()) ||
              isCreatingGroup
            }
            onClick={handleCreateGroup}
          >
            {isCreatingGroup ? <CircularProgress size={24} /> : "Create Group"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── VIEW GROUP DIALOG ───────────────────────────────────────────────── */}
      <Dialog
        open={viewGroupDialogOpen}
        onClose={() => setViewGroupDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <Info color="primary" />
            Group Details: {viewingGroup?.groupName}
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {viewingGroup?.agents?.length > 0 ? (
            <Stack spacing={2}>
              {viewingGroup.agents.map((agent, idx) => (
                <Paper key={idx} sx={{ p: 2, bgcolor: "#f8fafc" }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {agent.agentName || "Unnamed Agent"}
                  </Typography>
                  <Typography color="primary" sx={{ fontFamily: "monospace" }}>
                    {agent.agentNumber}
                  </Typography>
                </Paper>
              ))}
            </Stack>
          ) : (
            <Typography color="text.secondary">
              No agents in this group
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setViewGroupDialogOpen(false)}
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── SAVE TO LIST DIALOG ───────────────────────────────────────────────── */}
      <Dialog
        open={saveToListDialog}
        onClose={() => !isUploadingContacts && setSaveToListDialog(false)}
      >
        <DialogTitle>Save Contact List?</DialogTitle>
        <DialogContent>
          <Typography>
            {pendingContacts.length.toLocaleString()} valid contacts found
          </Typography>
          <Typography variant="body2" sx={{ mt: 2 }}>
            Would you like to save this list for future campaigns?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setSaveToListDialog(false)}
            disabled={isUploadingContacts}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleSaveToListConfirm(false)}
            variant="outlined"
            disabled={isUploadingContacts}
          >
            No, use once
          </Button>
          <Button
            onClick={() => handleSaveToListConfirm(true)}
            variant="contained"
            disabled={isUploadingContacts}
          >
            Yes, Save List
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ImprovedVoiceCampaignForm;
